import { Injectable, Inject, PLATFORM_ID, signal, effect } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError, firstValueFrom } from 'rxjs';
import { map, tap, catchError, shareReplay, timeout } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { UserProfile } from '../models';
import { AuthService } from './auth.service';
import { ImageFallbackService } from './image-fallback.service';
import { AppInitializerService } from './app-initializer.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private apiUrl = environment.apiUrl;

  private readonly PROFILE_KEY = 'user_profile';

  userProfile = signal<UserProfile | null>(null);

  private profileRequest$: Observable<UserProfile | null> | null = null;

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object,
    private authService: AuthService,
    private imageFallback: ImageFallbackService,
    private appInitializer: AppInitializerService,
  ) {
    effect(() => {
      if (!this.authService.isLoggedIn()) {
        this.clearProfile();
      }
    });

    this.recoverProfileFromStorage();
  }

  async initializeProfile(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      this.appInitializer.markAsInitialized();
      return;
    }

    if (!this.authService.checkAuth()) {
      this.appInitializer.markAsInitialized();
      return;
    }

    if (this.userProfile()) {
      this.appInitializer.markAsInitialized();
      this.validateTokenInBackground();
      return;
    }

    try {
      await this.loadProfile();
      this.appInitializer.markAsInitialized();
    } catch (error: any) {
      if (error.status === 401) {
        this.authService.clearAuth();
      }
      this.appInitializer.markAsError(error);
    }
  }

  private validateTokenInBackground(): void {
    this.http
      .get<any>(`${this.apiUrl}/user/profile`)
      .pipe(
        timeout(10000),
        catchError((error) => {
          if (error.status === 401 || error.status === 403) {
            this.authService.clearAuth();
            this.clearProfile();
          }
          return of(null);
        }),
      )
      .subscribe((response) => {
        if (response?.user) {
          const freshProfile = this.mapToUserProfile(response.user);
          this.userProfile.set(freshProfile);
          this.saveProfileToStorage(freshProfile);
        }
      });
  }

  private async loadProfile(): Promise<UserProfile | null> {
    try {
      const response = await firstValueFrom(
        this.http.get<any>(`${this.apiUrl}/user/profile`).pipe(
          timeout(10000),
          catchError((error) => {
            throw error;
          }),
        ),
      );

      if (!response?.user) return null;

      const userProfile = this.mapToUserProfile(response.user);
      this.userProfile.set(userProfile);
      this.saveProfileToStorage(userProfile);
      return userProfile;
    } catch (error) {
      this.userProfile.set(null);
      throw error;
    }
  }

  private mapToUserProfile(userData: any): UserProfile {
    // ✅ Siempre setea el estado real — true O false
    const isVerified = !!userData.email_verified_at;
    this.authService.emailVerified.set(isVerified);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('email_verified', String(isVerified));
    }

    const profile = userData.profile || {};
    return {
      id: userData.id,
      username: userData.username,
      username_is_temp: userData.username_is_temp,
      name: userData.name,
      email: userData.email,
      bio: profile.bio,
      theme: profile.theme,
      youtube_link: profile.youtube_link,
      facebook_link: profile.facebook_link,
      x_link: profile.x_link,
      discord_link: profile.discord_link,
      profile_picture_url: profile.profile_picture_url,
      profile_picture_disk: profile.profile_picture_disk,
      optimizedProfile: this.imageFallback.getConstructedUrl(
        profile.profile_picture_url,
        'profile',
      ),
      banner_url: profile.banner_url,
      banner_disk: profile.banner_disk,
      optimizedBanner: this.imageFallback.getConstructedUrl(
        profile.banner_url,
        'banner',
      ),
      is_pro: userData.is_pro ?? false,
      pro_expires_at: userData.pro_expires_at ?? null,
      subscription_status: userData.subscription_status ?? null,
      has_subscription: userData.has_subscription ?? false,
    };
  }

  getUserProfile(): Observable<UserProfile | null> {
    if (!isPlatformBrowser(this.platformId)) return of(null);
    if (!this.authService.checkAuth()) return of(null);

    const cachedProfile = this.userProfile();
    if (cachedProfile) return of(cachedProfile);

    if (this.profileRequest$) return this.profileRequest$;

    this.profileRequest$ = this.http
      .get<any>(`${this.apiUrl}/user/profile`)
      .pipe(
        timeout(10000),
        map((response) =>
          response?.user ? this.mapToUserProfile(response.user) : null,
        ),
        tap((user) => {
          this.userProfile.set(user);
          if (user) this.saveProfileToStorage(user);
          this.profileRequest$ = null;
        }),
        catchError((error) => {
          this.userProfile.set(null);
          this.profileRequest$ = null;
          if (error.status === 401 || error.status === 403) {
            this.authService.clearAuth();
          }
          return of(null);
        }),
        shareReplay(1),
      );

    return this.profileRequest$;
  }

  reloadProfile(): Observable<UserProfile | null> {
    this.userProfile.set(null);
    this.profileRequest$ = null;
    return this.getUserProfile();
  }

  updateUserProfile(updatedData: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/user/profile`, updatedData).pipe(
      tap((response) => {
        if (response.user) {
          const current = this.userProfile();
          const updated = this.mapToUserProfile(response.user);

          if (current) {
            updated.is_pro = current.is_pro;
          }

          this.userProfile.set(updated);
          this.saveProfileToStorage(updated);
        }
      }),
    );
  }

  /**
   * Configura el username por primera vez (login con Google — sin costo).
   */
  setUsername(username: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/user/username`, { username }).pipe(
      tap((response: any) => {
        const current = this.userProfile();
        if (current) {
          const updated = { ...current, username };
          this.userProfile.set(updated);
          this.saveProfileToStorage(updated);
        }
      }),
      catchError((error) => throwError(() => error)),
    );
  }

  /**
   * Cambia el username de un usuario con cuenta establecida.
   * El backend requiere plan PRO para este cambio.
   */
  updateUsername(username: string): Observable<any> {
    return this.http
      .put<any>(`${this.apiUrl}/user/username`, { username })
      .pipe(
        tap((response) => {
          if (response.user) {
            const userProfile = this.mapToUserProfile(response.user);
            this.userProfile.set(userProfile);
            this.saveProfileToStorage(userProfile);
          }
        }),
        catchError((error) => throwError(() => error)),
      );
  }

  getUserByUsername(username: string): Observable<any> {
    const cleanedUsername = username.replace(/^@/, '');
    const url = `${this.apiUrl}/user/profile/${encodeURIComponent(cleanedUsername)}`;
    const token = this.authService.token;

    const options = token
      ? { headers: new HttpHeaders({ Authorization: `Bearer ${token}` }) }
      : undefined;

    return this.http.get<any>(url, options);
  }

  checkUsername(username: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/check-username`, { username });
  }

  changePassword(data: {
    current_password: string;
    password: string;
    password_confirmation: string;
  }): Observable<any> {
    return this.http
      .put<any>(`${this.apiUrl}/user/change-password`, data)
      .pipe(catchError((error) => throwError(() => error)));
  }

  updateProfileImageUrl(
    type: 'profile_picture' | 'banner',
    url: string,
  ): Observable<any> {
    if (!this.isValidUrl(url))
      return throwError(() => new Error('URL inválida'));

    return this.http
      .put(`${this.apiUrl}/update-profile-url`, { type, url, use_proxy: true })
      .pipe(
        tap(() => this.reloadProfile().subscribe()),
        catchError((error) => throwError(() => error)),
      );
  }

  uploadProfileImage(formData: FormData): Observable<any> {
    return this.http
      .post(`${this.apiUrl}/upload-profile-image`, formData)
      .pipe(tap(() => this.reloadProfile().subscribe()));
  }

  getCurrentUser(): UserProfile | null {
    return this.userProfile();
  }

  /** ¿El usuario actual tiene plan PRO? */
  isProUser(): boolean {
    return this.userProfile()?.is_pro ?? false;
  }

  getUserId(): number | null {
    return this.userProfile()?.id ?? null;
  }

  /**
   * Elimina la cuenta del usuario autenticado.
   * @param password Contraseña de confirmación (requerida para cuentas normales,
   *                 opcional para cuentas vinculadas con Google).
   */
  deleteAccount(password?: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/user/account`, {
      body: password ? { password } : undefined,
    });
  }

  clearProfile(): void {
    this.userProfile.set(null);
    this.removeProfileFromStorage();
    this.profileRequest$ = null;
  }

  // ──────────────────────────────────────────
  // Storage helpers
  // ──────────────────────────────────────────

  private recoverProfileFromStorage(): void {
    if (!isPlatformBrowser(this.platformId) || !this.authService.checkAuth()) {
      this.removeProfileFromStorage();
      return;
    }

    try {
      const storedProfile = localStorage.getItem(this.PROFILE_KEY);
      if (storedProfile) {
        this.userProfile.set(JSON.parse(storedProfile) as UserProfile);
      }
    } catch {
      this.removeProfileFromStorage();
    }
  }

  private saveProfileToStorage(profile: UserProfile): void {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      localStorage.setItem(this.PROFILE_KEY, JSON.stringify(profile));
    } catch {
      /* quota exceeded — ignorar */
    }
  }

  private removeProfileFromStorage(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(this.PROFILE_KEY);
    }
  }

  private isValidUrl(url: string): boolean {
    try {
      const u = new URL(url);
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
      return false;
    }
  }
}
